<template>
  <div class="main-container">
    <v-card class="clock-in-card" elevation="3">
      <v-card-title class="header">Clock In</v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-text-field dense rounded filled v-model="clockData.name" label="Name" readonly></v-text-field>
          <v-text-field dense rounded filled v-model="clockData.email" label="Email" readonly></v-text-field>
          <v-menu
            v-model="menuClockInTime"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="clockData.clockInTime"
                label="Clock In Time"
                prepend-icon="mdi-clock"
                rounded
                filled
                dense
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-time-picker v-model="clockData.clockInTime" @input="menuClockInTime = false"></v-time-picker>
          </v-menu>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn v-if="!isClockedIn" dark color="red" rounded class="action-button" @click="clockIn">Clock In</v-btn>
        <v-btn v-else color="success" class="action-button" @click="clockOut">Clock Out</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Display Timer -->
    <v-card v-if="isClockedIn" class="clock-in-card" elevation="3">
      <v-card-title class="header">Time Spent</v-card-title>
      <v-card-text>
        <div><strong>Elapsed Time:</strong> {{ formattedTime }}</div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

const db = getFirestore(firebaseApp);

export default {
  data() {
    return {
      valid: false,
      clockData: {
        uid: "",
        name: "",
        email: "",
        clockInTime: "",
        clockOutTime: "",
      },
      isClockedIn: false,
      clockInDocId: null,
      timer: null,
      elapsedTime: 0,
      menuClockInTime: false,
    };
  },
  computed: {
    formattedTime() {
      const hours = Math.floor(this.elapsedTime / 3600);
      const minutes = Math.floor((this.elapsedTime % 3600) / 60);
      const seconds = this.elapsedTime % 60;
      return `${hours}h ${minutes}m ${seconds}s`;
    },
  },
  async created() {
    const auth = getAuth(firebaseApp);
    const user = auth.currentUser;
    if (user) {
      this.clockData.uid = user.uid;
      this.clockData.email = user.email;
      await this.fetchUserFullName(user.uid);
      await this.checkIfClockedIn(user.uid);
    }
  },
  methods: {
    async fetchUserFullName(uid) {
      try {
        const userDoc = await getDoc(doc(db, "users", uid));
        if (userDoc.exists()) {
          this.clockData.name = userDoc.data().fullName || "Unknown User";
        } else {
          console.error("No such user document!");
        }
      } catch (e) {
        console.error("Error fetching user full name: ", e);
      }
    },
    async clockIn() {
      const now = new Date();
      if (!this.clockData.clockInTime) {
        this.clockData.clockInTime = now.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      try {
        const docRef = await addDoc(collection(db, "Clock Time"), {
          ...this.clockData,
          date: now.toDateString(),
        });
        this.isClockedIn = true;
        this.clockInDocId = docRef.id;
        this.startTimer();
      } catch (e) {
        console.error("Error adding document: ", e);
      }
    },
    async clockOut() {
      const now = new Date();
      this.clockData.clockOutTime = now.toISOString();
      this.stopTimer();
      try {
        await updateDoc(doc(db, "Clock Time", this.clockInDocId), {
          clockOutTime: this.clockData.clockOutTime,
          elapsedTime: this.elapsedTime,
        });
        this.isClockedIn = false;
      } catch (e) {
        console.error("Error updating document: ", e);
      }
    },
    startTimer() {
      this.timer = setInterval(() => {
        this.elapsedTime++;
      }, 1000);
    },
    stopTimer() {
      clearInterval(this.timer);
    },
    async checkIfClockedIn(uid) {
      const today = new Date().toDateString();
      const q = query(
        collection(db, "Clock Time"),
        where("uid", "==", uid),
        where("date", "==", today)
      );
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.clockOutTime === "") {
          this.isClockedIn = true;
          this.clockInDocId = doc.id;
          this.clockData.clockInTime = data.clockInTime;
          this.elapsedTime = data.elapsedTime || 0;
          this.startTimer();
        }
      });
    },
  },
  beforeDestroy() {
    this.stopTimer();
  },
};
</script>

<style scoped>
.main-container {
  background-color: #121212;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.clock-in-card {
  background-color: #ffffff;
  border-radius: 16px;
  width: 400px;
  padding: 24px;
  margin: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.header {
  text-align: center;
  color: #ff3b47;
  font-weight: bold;
}

.action-button {
  margin: 0 auto;
  display: block;
  width: 200px;
}
</style>
